import Enumerable from "linq";
import { Directory, RootDirectory } from "../../../../../domain/drawing";
import {
  FormatReference,
  FormatSet,
  IFormatReferenceTreeNode,
  IFormatReferenceTreeData,
  IFormatSetTreeNode,
  IFormatSetTreeData,
} from "../../../../../domain/format";

export interface IExpandableTreeNodeIconClasses {
  normal: string;
  expanded: string;
}

export interface IFormatTreeNodeIconClasses {
  directory: IExpandableTreeNodeIconClasses;
  formatSet: IExpandableTreeNodeIconClasses;
  format: string;
}

type IFormatRelatedTreeData = IFormatReferenceTreeData | IFormatSetTreeData;
type IFormatRelatedTreeNode = IFormatReferenceTreeNode | IFormatSetTreeNode;

export class FormatTreeNodeUtility {
  static defaultIconClasses: IFormatTreeNodeIconClasses = {
    directory: {
      normal: "tree-icon-directory",
      expanded: "tree-icon-directory-expand",
    },
    formatSet: {
      normal: "tree-icon-format-set",
      expanded: "tree-icon-format-set-expand",
    },
    format: "tree-icon-format",
  };

  constructor(iconClasses?: IFormatTreeNodeIconClasses) {
    this._iconClasses = !iconClasses
      ? FormatTreeNodeUtility.defaultIconClasses
      : iconClasses;
  }

  private readonly _iconClasses: IFormatTreeNodeIconClasses;

  findNode(node: IFormatReferenceTreeNode, predicate: (n: IFormatReferenceTreeNode) => boolean): IFormatReferenceTreeNode {
    return predicate(node)
      ? node
      : Enumerable.from(node.childNodes).select(n => this.findNode(n, predicate)).firstOrDefault(n => !!n);
  }

  toFormatSetNode(entity: FormatSet, index: number): IFormatSetTreeData {
    return {
      id: index,
      label: entity.name,
      iconClass: this._iconClasses.formatSet.normal,
      source: entity,
      children: [],
    };
  }

  toFormatNode(entity: FormatReference, index: number): IFormatReferenceTreeData {
    return {
      id: index,
      label: entity.name,
      iconClass: this._iconClasses.format,
      source: entity,
      children: [],
    };
  }

  expandNode(node: IFormatRelatedTreeNode): void {
    this.applyExpandStatusToNode(node, true);
  }

  collapseNode(node: IFormatRelatedTreeNode): void {
    this.applyExpandStatusToNode(node, false);
  }

  private applyExpandStatusToNode(node: IFormatRelatedTreeNode, expanded: boolean): void {
    if (node.childNodes instanceof Array && node.childNodes.length > 0) {
      if (node.data instanceof Object) {
        if (node.data.source instanceof FormatSet) {
          // 文書セットは配下に要素は無いのでここは通らないはずだが、念のため実装を残している
          node.expanded = expanded;
          node.data.iconClass = expanded
            ? this._iconClasses.formatSet.expanded
            : this._iconClasses.formatSet.normal;
        }
        if (node.data.source instanceof Directory) {
          node.expanded = expanded;
          node.data.iconClass = expanded
            ? this._iconClasses.directory.expanded
            : this._iconClasses.directory.normal;
        }
      }
      node.childNodes.forEach((t: IFormatRelatedTreeNode) => this.applyExpandStatusToNode(t, expanded));
    }
  }

  updateNodeExpanded(node: IFormatRelatedTreeData, nodeProperty: IFormatRelatedTreeNode, nodeSelf: any): void {
    if (nodeProperty.data.source instanceof FormatSet) {
      // nodeSelfは直前の状態の模様。そのため判定は逆になる
      node.iconClass = nodeSelf.expanded
        ? this._iconClasses.formatSet.normal
        : this._iconClasses.formatSet.expanded;
    }
    if (nodeProperty.data.source instanceof Directory) {
      node.iconClass = nodeSelf.expanded
        ? this._iconClasses.directory.normal
        : this._iconClasses.directory.expanded;
    }
  }

  nodeIconClass(node: IFormatRelatedTreeNode): string {
    if (node.data instanceof Object) {
      if (node.data.source instanceof FormatSet) {
        return node.expanded
          ? this._iconClasses.formatSet.expanded
          : this._iconClasses.formatSet.normal;
      }
      if (node.data.source instanceof Directory) {
        return node.expanded
          ? this._iconClasses.directory.expanded
          : this._iconClasses.directory.normal;
      }
      return this._iconClasses.format;
    }
    return "";
  }

  toFormatReferenceDirectories(folders: IFormatReferenceTreeData[]): Directory<string, FormatReference>[] {
    return Enumerable.from(folders)
      .select((f) => this.toFormatReferenceDirectory(f))
      .toArray();
  }

  private toFormatReferenceDirectory(folder: IFormatReferenceTreeData): Directory<string, FormatReference> {
    return folder.source instanceof Directory ? new Directory(
      folder.source.key,
      folder.source.name,
      this.toFormatReferenceDirectories(
        Enumerable.from(folder.children)
          .where(f => f.source instanceof Directory)
          .toArray()),
      this.toFormatReferences(
          Enumerable.from(folder.children)
          .where(f => f.source instanceof FormatReference)
          .toArray()),
      null,
      folder.source.recordId
    ) : null;
  }

  private toFormatReferences(phrases: IFormatReferenceTreeData[]): FormatReference[] {
    return Enumerable.from(phrases)
      .select(p => this.toFormatReference(p))
      .toArray();
  }

  private toFormatReference(format: IFormatReferenceTreeData): FormatReference {
    return format.source instanceof FormatReference ? new FormatReference(
      format.source.id,
      format.source.name,
      format.source.previewSource ? format.source.previewSource.clone() : null,
      format.source.thumbnailSource ? format.source.thumbnailSource.clone() : null,
      format.source.reportTypeId,
      format.source.masterCode,
      format.source.tag,
      format.source.company ? format.source.company.clone() : null,
      format.source.categoryType,
      format.source.writeMethodType,
      format.source.startDateTime,
      format.source.endDateTime,
      format.source.expired,
      format.source.deleted,
      format.source.editedName,
      format.source.originalName,
      format.source.note,
      format.source.quoteBehaviorKind,
    ) : null;
  }

  toFormatSetRootDirectory(items: IFormatSetTreeData[]): RootDirectory<string, FormatSet> {
    const nodes = this.toFormatSetDirectories(Enumerable.from(items)
      .where(i => i.source instanceof Directory)
      .toArray());
    const entities = this.toFormatSets(Enumerable.from(items)
      .where(i => i.source instanceof FormatSet)
      .toArray());
    return new RootDirectory(nodes, entities);
  }

  toFormatSetDirectories(folders: IFormatSetTreeData[]): Directory<string, FormatSet>[] {
    return Enumerable.from(folders)
      .select((f) => this.toFormatSetDirectory(f))
      .toArray();
  }

  private toFormatSetDirectory(folder: IFormatSetTreeData): Directory<string, FormatSet> {
    return folder.source instanceof Directory ? new Directory(
      folder.source.key,
      folder.source.name,
      this.toFormatSetDirectories(
        Enumerable.from(folder.children)
          .where(f => f.source instanceof Directory)
          .toArray()),
      this.toFormatSets(
          Enumerable.from(folder.children)
          .where(f => f.source instanceof FormatSet)
          .toArray()),
      null,
      folder.source.recordId
    ) : null;
  }

  private toFormatSets(phrases: IFormatSetTreeData[]): FormatSet[] {
    return Enumerable.from(phrases)
      .select(p => this.toFormatSet(p))
      .toArray();
  }

  private toFormatSet(formatSet: IFormatSetTreeData): FormatSet {
    return formatSet.source instanceof FormatSet
      ? new FormatSet(formatSet.source.id, formatSet.source.name, formatSet.source.formats, formatSet.source.recordId)
      : null;
  }
}
